/**
 * WeekPlanner - Main week view showing Mon-Fri with time slots
 *
 * The primary lesson planning interface for QuickGrade.
 *
 * Features:
 * - Header: Week selector (< Week of Jan 20-24 >)
 * - Grid: Days as columns, time blocks as rows
 * - Each cell shows: subject, outcome tags, attached resources
 * - Click to open BlockDetail modal
 * - Mobile-responsive (stacks days on mobile)
 */

import { useState, useMemo, useCallback, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { Plus, Calendar, ChevronDown, ChevronUp } from "lucide-react";
import { WeekNavigation } from "./WeekNavigation";
import { TimeBlock } from "./TimeBlock";
import { BlockDetail } from "./BlockDetail";
import {
  type TimeBlock as TimeBlockType,
  type CurriculumOutcome,
  type DayOfWeek,
  type WeekPlan,
  type TimeSlot,
  DAYS_OF_WEEK,
  DAY_ABBREVIATIONS,
  DEFAULT_TIME_SLOTS,
  getWeekStart,
  getWeekDates,
  formatTime,
} from "./types";

// ============================================================================
// Types
// ============================================================================

export interface WeekPlannerProps {
  /** Initial week to display (defaults to current week) */
  initialWeek?: Date;
  /** Week plan data */
  weekPlan?: WeekPlan | null;
  /** Curriculum outcomes for the outcome picker */
  outcomes?: CurriculumOutcome[];
  /** Time slots configuration */
  timeSlots?: TimeSlot[];
  /** Loading state */
  isLoading?: boolean;
  /** Called when week changes */
  onWeekChange?: (weekStart: Date) => void;
  /** Called when a block is created or updated */
  onBlockSave?: (block: Omit<TimeBlockType, "id"> & { id?: string }) => void;
  /** Called when a block is deleted */
  onBlockDelete?: (blockId: string) => void;
  /** Additional class name */
  className?: string;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get blocks for a specific day and time slot
 */
function getBlocksForSlot(
  blocks: TimeBlockType[],
  day: DayOfWeek,
  slot: TimeSlot
): TimeBlockType[] {
  return blocks.filter((block) => {
    if (block.day !== day) return false;
    // Check if block overlaps with slot
    const blockStart = block.startTime;
    const blockEnd = block.endTime;
    const slotStart = slot.startTime;
    const slotEnd = slot.endTime;
    // Block overlaps if it starts before slot ends AND ends after slot starts
    return blockStart < slotEnd && blockEnd > slotStart;
  });
}

// ============================================================================
// Sub-Components
// ============================================================================

interface DayHeaderProps {
  day: DayOfWeek;
  date: Date;
  isToday: boolean;
}

function DayHeader({ day, date, isToday }: DayHeaderProps) {
  const dayNum = date.getDate();

  return (
    <div
      className={cn(
        "flex flex-col items-center py-2 px-1",
        isToday && "bg-primary/5 rounded-t-md"
      )}
    >
      <span
        className={cn(
          "text-xs font-medium uppercase",
          isToday ? "text-primary" : "text-muted-foreground"
        )}
      >
        {DAY_ABBREVIATIONS[day]}
      </span>
      <span
        className={cn(
          "text-lg font-semibold",
          isToday && "bg-primary text-primary-foreground rounded-full w-8 h-8 flex items-center justify-center"
        )}
      >
        {dayNum}
      </span>
    </div>
  );
}

interface MobileDayViewProps {
  day: DayOfWeek;
  date: Date;
  isToday: boolean;
  blocks: TimeBlockType[];
  timeSlots: TimeSlot[];
  outcomesMap: Map<string, CurriculumOutcome>;
  onBlockClick: (block: TimeBlockType | null, day: DayOfWeek, slot: TimeSlot) => void;
  isExpanded: boolean;
  onExpandToggle: () => void;
}

function MobileDayView({
  day,
  date,
  isToday,
  blocks,
  timeSlots,
  outcomesMap,
  onBlockClick,
  isExpanded,
  onExpandToggle,
}: MobileDayViewProps) {
  const dayBlocks = blocks.filter((b) => b.day === day);
  const dayNum = date.getDate();
  const monthName = date.toLocaleDateString("en-US", { month: "short" });

  return (
    <Card className={cn(isToday && "ring-2 ring-primary")}>
      <button
        type="button"
        onClick={onExpandToggle}
        className="w-full text-left"
        aria-expanded={isExpanded}
      >
        <CardHeader className="py-3 px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className={cn(
                  "text-center min-w-[44px]",
                  isToday && "bg-primary text-primary-foreground rounded-lg p-1"
                )}
              >
                <div className="text-xs font-medium uppercase">
                  {DAY_ABBREVIATIONS[day]}
                </div>
                <div className="text-lg font-bold">{dayNum}</div>
              </div>
              <div>
                <CardTitle className="text-base">
                  {day}, {monthName} {dayNum}
                </CardTitle>
                <p className="text-xs text-muted-foreground">
                  {dayBlocks.length} block{dayBlocks.length !== 1 ? "s" : ""}
                </p>
              </div>
            </div>
            {isExpanded ? (
              <ChevronUp className="h-5 w-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-5 w-5 text-muted-foreground" />
            )}
          </div>
        </CardHeader>
      </button>

      {isExpanded && (
        <CardContent className="pt-0 pb-4 px-4 space-y-2">
          {timeSlots
            .filter((slot) => !["Recess", "Lunch"].includes(slot.label))
            .map((slot) => {
              const slotBlocks = getBlocksForSlot(blocks, day, slot);
              const block = slotBlocks[0] || null;

              return (
                <div key={slot.startTime} className="flex gap-2">
                  <div className="text-xs text-muted-foreground w-12 pt-2 text-right">
                    {formatTime(slot.startTime).replace(" AM", "a").replace(" PM", "p")}
                  </div>
                  <div className="flex-1">
                    <TimeBlock
                      block={block}
                      startTime={slot.startTime}
                      endTime={slot.endTime}
                      outcomesMap={outcomesMap}
                      onClick={() => onBlockClick(block, day, slot)}
                    />
                  </div>
                </div>
              );
            })}

          <Button
            variant="outline"
            size="sm"
            className="w-full mt-2"
            onClick={() =>
              onBlockClick(null, day, {
                label: "Custom",
                startTime: "08:45",
                endTime: "09:30",
              })
            }
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Custom Block
          </Button>
        </CardContent>
      )}
    </Card>
  );
}

// ============================================================================
// Loading Skeleton
// ============================================================================

function WeekPlannerSkeleton() {
  return (
    <div className="space-y-4">
      <div className="flex justify-center">
        <Skeleton className="h-10 w-64" />
      </div>
      <div className="hidden md:grid md:grid-cols-6 gap-2">
        <Skeleton className="h-8" />
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-8" />
        ))}
        {Array.from({ length: 30 }).map((_, i) => (
          <Skeleton key={i} className="h-20" />
        ))}
      </div>
      <div className="md:hidden space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-24" />
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function WeekPlanner({
  initialWeek,
  weekPlan,
  outcomes = [],
  timeSlots = DEFAULT_TIME_SLOTS,
  isLoading = false,
  onWeekChange,
  onBlockSave,
  onBlockDelete,
  className,
}: WeekPlannerProps) {
  // State
  const [weekStart, setWeekStart] = useState<Date>(() =>
    initialWeek ? getWeekStart(initialWeek) : getWeekStart(new Date())
  );
  const [selectedBlock, setSelectedBlock] = useState<TimeBlockType | null>(null);
  const [selectedDay, setSelectedDay] = useState<DayOfWeek>("Monday");
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [expandedDays, setExpandedDays] = useState<Set<DayOfWeek>>(new Set());

  // Get week dates
  const weekDates = useMemo(() => getWeekDates(weekStart), [weekStart]);

  // Check which day is today
  const today = useMemo(() => new Date(), []);
  const todayDayIndex = useMemo(() => {
    const dayOfWeek = today.getDay();
    // Sunday = 0, Monday = 1, etc. Convert to our index (0 = Monday)
    return dayOfWeek === 0 ? -1 : dayOfWeek - 1;
  }, [today]);

  // Is a given date today?
  const isDateToday = useCallback(
    (date: Date) => {
      return (
        date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear()
      );
    },
    [today]
  );

  // Auto-expand today on mobile
  useEffect(() => {
    if (todayDayIndex >= 0 && todayDayIndex < 5) {
      setExpandedDays(new Set([DAYS_OF_WEEK[todayDayIndex]]));
    }
  }, [todayDayIndex]);

  // Create outcomes map for quick lookup
  const outcomesMap = useMemo(
    () => new Map(outcomes.map((o) => [o.id, o])),
    [outcomes]
  );

  // Get blocks for current week
  const blocks = weekPlan?.blocks || [];

  // Handle week change
  const handleWeekChange = useCallback(
    (newWeekStart: Date) => {
      setWeekStart(newWeekStart);
      onWeekChange?.(newWeekStart);
    },
    [onWeekChange]
  );

  // Handle block click
  const handleBlockClick = useCallback(
    (block: TimeBlockType | null, day: DayOfWeek, slot: TimeSlot) => {
      setSelectedBlock(block);
      setSelectedDay(day);
      setSelectedSlot(slot);
      setIsDetailOpen(true);
    },
    []
  );

  // Handle block save
  const handleBlockSave = useCallback(
    (blockData: Omit<TimeBlockType, "id"> & { id?: string }) => {
      onBlockSave?.(blockData);
    },
    [onBlockSave]
  );

  // Handle block delete
  const handleBlockDelete = useCallback(
    (blockId: string) => {
      onBlockDelete?.(blockId);
    },
    [onBlockDelete]
  );

  // Toggle mobile day expansion
  const handleDayExpandToggle = useCallback((day: DayOfWeek) => {
    setExpandedDays((prev) => {
      const next = new Set(prev);
      if (next.has(day)) {
        next.delete(day);
      } else {
        next.add(day);
      }
      return next;
    });
  }, []);

  // Filter out recess/lunch for grid display
  const gridTimeSlots = useMemo(
    () => timeSlots.filter((slot) => !["Recess", "Lunch"].includes(slot.label)),
    [timeSlots]
  );

  if (isLoading) {
    return (
      <div className={cn("space-y-4", className)}>
        <WeekPlannerSkeleton />
      </div>
    );
  }

  return (
    <div className={cn("space-y-4", className)}>
      {/* Week Navigation */}
      <div className="flex items-center justify-between">
        <WeekNavigation weekStart={weekStart} onWeekChange={handleWeekChange} />
        <Button variant="outline" size="sm" className="hidden sm:flex">
          <Plus className="h-4 w-4 mr-2" />
          New Block
        </Button>
      </div>

      {/* Desktop Grid View */}
      <div className="hidden md:block overflow-x-auto">
        <div className="min-w-[600px]" role="grid" aria-label="Weekly schedule">
          {/* Grid Header */}
          <div
            className="grid grid-cols-[60px_repeat(5,1fr)] gap-1 mb-1"
            role="row"
          >
            {/* Time column header */}
            <div
              className="flex items-center justify-center py-2"
              role="columnheader"
              aria-label="Time"
            >
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </div>
            {/* Day headers */}
            {DAYS_OF_WEEK.map((day, idx) => (
              <div key={day} role="columnheader">
                <DayHeader
                  day={day}
                  date={weekDates[idx]}
                  isToday={isDateToday(weekDates[idx])}
                />
              </div>
            ))}
          </div>

          {/* Grid Body */}
          <div className="space-y-1">
            {gridTimeSlots.map((slot) => (
              <div
                key={slot.startTime}
                className="grid grid-cols-[60px_repeat(5,1fr)] gap-1"
                role="row"
              >
                {/* Time label */}
                <div
                  className="flex items-center justify-end pr-2 text-xs text-muted-foreground"
                  role="rowheader"
                >
                  {formatTime(slot.startTime).replace(" AM", "").replace(" PM", "")}
                </div>

                {/* Day cells */}
                {DAYS_OF_WEEK.map((day, idx) => {
                  const slotBlocks = getBlocksForSlot(blocks, day, slot);
                  const block = slotBlocks[0] || null;
                  const isToday = isDateToday(weekDates[idx]);

                  return (
                    <div
                      key={`${day}-${slot.startTime}`}
                      className={cn(isToday && "bg-primary/5 rounded-md")}
                      role="gridcell"
                      aria-label={`${day} at ${formatTime(slot.startTime)}${block ? `: ${block.title || block.subject || "Untitled block"}` : ""}`}
                    >
                      <TimeBlock
                        block={block}
                        startTime={slot.startTime}
                        endTime={slot.endTime}
                        outcomesMap={outcomesMap}
                        onClick={() => handleBlockClick(block, day, slot)}
                      />
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Mobile Stacked View */}
      <div className="md:hidden space-y-3">
        {DAYS_OF_WEEK.map((day, idx) => (
          <MobileDayView
            key={day}
            day={day}
            date={weekDates[idx]}
            isToday={isDateToday(weekDates[idx])}
            blocks={blocks}
            timeSlots={timeSlots}
            outcomesMap={outcomesMap}
            onBlockClick={handleBlockClick}
            isExpanded={expandedDays.has(day)}
            onExpandToggle={() => handleDayExpandToggle(day)}
          />
        ))}
      </div>

      {/* Empty state */}
      {blocks.length === 0 && (
        <Card className="p-8 text-center">
          <Calendar className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
          <h3 className="text-lg font-semibold mb-2">No blocks yet</h3>
          <p className="text-muted-foreground mb-4">
            Click on any time slot to add your first lesson block.
          </p>
          <Button
            onClick={() =>
              handleBlockClick(null, "Monday", {
                label: "Block 1",
                startTime: "08:45",
                endTime: "09:30",
              })
            }
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Your First Block
          </Button>
        </Card>
      )}

      {/* Block Detail Modal */}
      <BlockDetail
        open={isDetailOpen}
        onOpenChange={setIsDetailOpen}
        block={selectedBlock}
        day={selectedDay}
        defaultStartTime={selectedSlot?.startTime}
        defaultEndTime={selectedSlot?.endTime}
        outcomes={outcomes}
        onSave={handleBlockSave}
        onDelete={handleBlockDelete}
      />
    </div>
  );
}
